import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Link, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { whatsappRoutes } from '../../Whatsapp/Constant';
import { sitePrefix } from '../../../config';
import {
    selectWaActive, selectWaOnly, selectWaReceiptState,
    selectSelectedSourceRow, channelsFrom, activeChannelKeys, ChannelKey, WaRootState,
} from './selectors';
import { WaSendOutcome } from '../../../Models/DataSources/SmartSendWhatsApp';
import useWaErrorText from './errorText';

// SmartSend part B — the three receipt lines of the result phase: e-mail, WhatsApp, supervisor.
//
// 🔴 THE WHATSAPP LINE DESCRIBES WHAT THE SERVER HOLDS FOR THE UNIT, not only what the promise
// said: `SendSummaryDialog` re-reads the unit (by its own id, channel 3) after every answer. The
// response interceptor (`PulseemReactAPI.ts:74-79`) reads `error.response.status` without a guard,
// so a timeout or a network drop arrives as `StatusCode: 500` with an empty payload —
// indistinguishable from a real failure unless the server's own state is consulted.
// 09-channel-units — THE E-MAIL LINE ON A "BOTH" PAGE follows the send's own code (`emailCode`):
// the re-read is the WhatsApp unit's, and a unit carries no e-mail status (_GetMapping_Stage leaves
// EmailCampaignStatus NULL on channel 3). A lost response on "both" therefore still reads "not
// sent" — the same owner-deferred ambiguity (B4) as an e-mail-only send.

const useStyles = makeStyles((theme) => ({
    wrap: { marginTop: theme.spacing(2), display: 'flex', flexDirection: 'column', gap: theme.spacing(1) },
    row: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), flexWrap: 'wrap' },
    label: { fontWeight: 600, minWidth: 90 },
    links: { display: 'flex', gap: theme.spacing(2), flexWrap: 'wrap', marginTop: theme.spacing(1) },
    bad: { color: 'var(--pls-d-danger, #8a1c1c)' },
}));

interface Props {
    campaignId: number;
    /** The e-mail pipeline's own HTTP code, as `sendSmart` reported it. */
    emailCode: number | null;
    /** Whether the operator asked for a supervisor report. */
    sentToSupervisor: boolean;
    supervisorRequestId: number | null;
}

const WaReceiptLines: React.FC<Props> = ({ campaignId, emailCode, sentToSupervisor, supervisorRequestId }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { waErrorText } = useWaErrorText();

    const active = useSelector((s: WaRootState) => selectWaActive(s));
    const outcome: WaSendOutcome | null = useSelector((s: any) => s.smartSendWa.waSendOutcome);
    const mapping = useSelector((s: any) => s.smartSendWa.waMapping);
    const receiptState = useSelector((s: WaRootState) => selectWaReceiptState(s));
    // 09-channel-units — "this page IS the WhatsApp unit" (`unitMode` 'whatsapp', no e-mail in the
    // send). It changes ONE line of this receipt, and only its wording.
    const waOnly = useSelector((s: WaRootState) => selectWaOnly(s));
    // 🔴 v1.23h · C-22 — AND WHETHER THAT LINE EXISTS AT ALL.
    //
    // `receipt.emailSuppressed` ("לא נשלח — המייל כובה בקמפיין הזה") exists to stop the operator
    // reading a missing mail as a FAILED mail. That worry only exists where there was a mail
    // that could have gone out. On a phone-only source there never was one, and the sentence
    // then implies the opposite of the truth — it says a mail was turned off, when the campaign
    // never had an address to send one to.
    //
    // ⚠️ THE DISTINCTION FROM C-13, WHICH IS WHAT STOPS THIS BEING "FIXED" BACK LATER:
    //   · a channel that EXISTED and was switched off ⇒ REPORT it (that is why
    //     `emailSuppressed` exists at all);
    //   · a channel that was never in the picture   ⇒ do not render it.
    // Two different states, two different answers.
    //
    // ⚠️ AND `null` MEANS UNKNOWN, NOT "NO". `selectSelectedSourceRow` answers null when the
    // source list has not loaded — and this is the OPPOSITE direction to the removal dialog's
    // `selectSourceHasEmailIdentity`, which treats unknown as "no" on purpose. There, unknown
    // withholds an OPTION and asserts nothing; here it would withhold a FACT the operator needs.
    // So an unknown source keeps the line, and only an explicit `HasEmailIdentity === false`
    // removes it.
    const sourceRow = useSelector((s: WaRootState) => selectSelectedSourceRow(s));
    const showEmailLine = !(sourceRow && sourceRow.HasEmailIdentity === false);
    // 09-channel-units — which units the page carries, and the WhatsApp unit's own id (the retry's
    // target — never `campaignId`, which is the e-mail campaign on a "both" page). Read HERE, above
    // the early return below: a hook after a conditional return breaks the rules of hooks.
    const unitMode: string | null = useSelector((s: any) => s.smartSendWa.unitMode ?? null);
    // 🔴 C-45 · AND THERE IS NO SECOND CONDITION. `showEmailLine` ALONE DECIDES.
    //
    // A `const emailLineChannelOn = !waOnly` stood here and was ANDed into the set below, so on
    // a WhatsApp-only send the e-mail row disappeared entirely — and `receipt.emailSuppressed`
    // ("לא נשלח — המייל כובה בקמפיין הזה"), whose ONLY reachable state is that one, became dead
    // code sitting two screens up at the `waOnly` arm of `emailText`.
    //
    // 🔴 IT WAS THE EXACT INVERSE OF THE RULE WRITTEN FOUR LINES ABOVE IT. "A channel that
    // EXISTED and was switched off ⇒ REPORT it." WhatsApp-only IS that case: the operator had
    // an e-mail channel and turned it off herself. The case that must NOT render is C-22's —
    // a source with no address column, where there was never a mail to speak of — and
    // `showEmailLine` already answers exactly that, and only that.
    //
    // ⚠️ AND THIS IS WHY A COMMENT STATING THE RIGHT RULE IS NOT A GUARD. Both the rule and its
    // inverse lived in the same paragraph for a round. What holds it now is the render test.

    if (!active) return null;

    const emailStatus = mapping?.EmailCampaignStatus ?? null;
    // 09-channel-units — ON A "BOTH" PAGE THE MAPPING IS THE WHATSAPP UNIT'S, and a unit reads no
    // e-mail campaign (its GetMapping leaves EmailCampaignStatus NULL on channel 3), so there the
    // e-mail's own answer decides: `PUT Send` said 200/201. The leg's e-mail status stays the
    // authority only where a leg is what the mapping describes.
    const emailWentOut = unitMode === 'both'
        ? (emailCode === 200 || emailCode === 201)
        : (emailStatus === 2 || emailStatus === 4);

    // ── the WhatsApp line ───────────────────────────────────────────────────────────────────
    // FIRST MATCH WINS and the order is the contract's (§7 / v1.15): stuck → queued → inFlight.
    // `inFlight` is defined as the COMPLEMENT of `stuck`, so without a fixed order it overlaps
    // "queued" and two receipts light on one state.
    const waText = (): { text: string; bad?: boolean } => {
        if (outcome?.State === 'Skipped') {
            if (outcome.Reason === 'EMAIL_NOT_SENT') return { text: t('DataSources.send.whatsapp.receipt.emailNotSent'), bad: true };
            if (outcome.Reason === 'NO_CELL_IDENTITY') return { text: t('DataSources.send.whatsapp.receipt.skippedNoCell'), bad: true };
            if (outcome.Reason === 'NO_RECIPIENTS') return { text: t('DataSources.send.whatsapp.receipt.skippedNoRecipients'), bad: true };
            // The leg exists and the operator switched the channel off. REPORTED, because the rule
            // at :69-73 is "a channel that EXISTED and was switched off is reported".
            // ⚠️ AND WITHOUT `bad`, for the same reason `emailSuppressed` (:177) has none. Red
            // (#8a1c1c) states a fault; this is the operator's own decision carried out.
            if (outcome.Reason === 'CHANNEL_NOT_ACTIVE') return { text: t('DataSources.send.whatsapp.receipt.channelOff') };
            // NO_LEG: this campaign simply has no WhatsApp. Nothing to report.
            return { text: '' };
        }
        if (outcome?.State === 'Failed') {
            if (outcome.Reason === 'INTERNAL_AFTER_DISPATCH') {
                // The messages MAY have gone out. Never offer a retry here — it would charge and
                // re-send whatever already left.
                return { text: t('DataSources.send.whatsapp.receipt.internalAfterDispatch'), bad: true };
            }
            // Charged with nothing dispatched — the balance was deducted and not refunded.
            if (outcome.Charged && (outcome.Live ?? 0) === 0) {
                return { text: t('DataSources.send.whatsapp.receipt.chargedNoSend'), bad: true };
            }
            // 09-channel-units — the same fact read off the unit itself: a send refused BEFORE it ran
            // because an earlier attempt was charged and never refunded (the money gate's
            // WA_ALREADY_SENT). "Request a refund" is the remedy; "already sent" would not be true.
            if (mapping?.WaCampaignStatus === 1 && mapping?.LastErrorCharged) {
                return { text: t('DataSources.send.whatsapp.receipt.chargedNoSend'), bad: true };
            }
            // `receipt.failed` carries the reason: "WhatsApp was not sent: <reason>. The e-mail
            // was sent as usual." The reason itself is the §4 `Message` translated through
            // `error.*` — never the raw server string.
            // 09-channel-units — on the WhatsApp unit's own page there is no e-mail in the send, so
            // the same reason without that second sentence (`receipt.failedAlone`).
            return {
                text: t(unitMode === 'whatsapp'
                    ? 'DataSources.send.whatsapp.receipt.failedAlone'
                    : 'DataSources.send.whatsapp.receipt.failed', {
                    reason: waErrorText(outcome.Message),
                }),
                bad: true,
            };
        }
        // A live header: the SERVER's state decides, not the outcome held in memory — which is
        // what makes this correct after a refresh and after a timeout.
        if (receiptState === 'stuck') return { text: t('DataSources.send.whatsapp.receipt.stuck'), bad: true };
        if (receiptState === 'queued') return { text: t('DataSources.send.whatsapp.receipt.queued', { n: (outcome?.Queued ?? mapping?.WaRowsQueued ?? 0).toLocaleString() }) };
        if (receiptState === 'inFlight') return { text: t('DataSources.send.whatsapp.receipt.inFlight') };
        if (receiptState === 'sent') return { text: t('DataSources.send.whatsapp.receipt.sent') };
        // Also after a timeout: charged, and the CATCH's RS1 never reached the client.
        if (mapping?.WaCampaignStatus === 1 && mapping?.LastErrorCharged) {
            return { text: t('DataSources.send.whatsapp.receipt.chargedNoSend'), bad: true };
        }
        if (outcome?.State === 'Queued') return { text: t('DataSources.send.whatsapp.receipt.queued', { n: (outcome.Queued ?? 0).toLocaleString() }) };
        return { text: '' };
    };

    const wa = waText();

    // 550 (held for approval) and 551 (account awaiting approval) are different states with
    // different remedies: 550's campaign is `ApprovePending` and the WhatsApp unit is sent later
    // on its own, from its row in "Send-from-source campaigns" (`receipt.emailHeld` says so);
    // 551's campaign stays `Created` and the whole send is repeated.
    // 🔴 v1.23 — "המייל לא יצא" IS NOT A FAILURE WHEN THE OPERATOR TURNED IT OFF.
    //
    // The new arm sits AFTER `emailWentOut` and BEFORE the fallback, and both halves of that
    // placement carry weight:
    //  · after, because a campaign whose mail genuinely went out earlier must still say "נשלח"
    //    — the suppression is a fact about the NEXT send, not a retraction of the last one;
    //  · before, because the fallback is the only other arm that can be reached here, and it
    //    would state the operator's own decision as a fault.
    // Nothing else on this line moves: 550 and 551 are still their own states, and neither is
    // reachable on a WhatsApp-only send in the first place (no `SendCampaign` runs).
    //
    // ⚠️ THE OTHER `emailNotSent` IN THIS FILE IS NOT TOUCHED. On the WHATSAPP line,
    // `outcome.Reason === 'EMAIL_NOT_SENT'` means the WhatsApp was skipped BECAUSE the mail
    // failed, and that is true whenever it happens. On a WhatsApp-only send it cannot happen at
    // all — `PUT WhatsAppOnly/Send` (the unit's own route) sends no mail and asks nothing of one.
    const emailText = emailCode === 550
        ? t('DataSources.send.whatsapp.receipt.emailHeld')
        : emailCode === 551
            ? t('DataSources.send.whatsapp.receipt.emailPendingApproval')
            : emailWentOut
                ? t('DataSources.send.whatsapp.receipt.sent')
                : waOnly
                    ? t('DataSources.send.whatsapp.receipt.emailSuppressed')
                    : t('DataSources.send.whatsapp.receipt.emailNotSent');

    // 12-fix-wave 7.5 (D2) — NO RETRY OF A SENT SEND. A "both" send whose e-mail went out and whose
    // WhatsApp failed is closed: the result says so in the 7.10 "partial failure" text, with no button.
    // Not on INTERNAL_AFTER_DISPATCH, where the messages may have gone out.
    const partialFailure = unitMode === 'both' && emailWentOut
        && outcome?.State === 'Failed' && outcome.Reason !== 'INTERNAL_AFTER_DISPATCH';

    // ══ ONE ROW PER ACTIVE CHANNEL, BUILT IN A LOOP ══════════════════════════════════════════
    //
    // 🔴 WHAT THIS REPLACES, AND WHY IT MATTERS MORE THAN IT LOOKS. The receipt used to be two
    // hand-written blocks — an e-mail one and a WhatsApp one — each with its own visibility
    // rule inlined next to its own markup. That shape has no place to PUT a third channel: SMS
    // would arrive as a third copy of the same JSX, and the day one of the three grew a rule
    // the other two did not, nothing would say so.
    //
    // Now: `activeChannelKeys` decides WHICH rows exist, and one builder per channel decides
    // what each says. Adding SMS is adding an entry to `ROW` and NOTHING ELSE — `CHANNEL_ORDER`
    // is derived from `CHANNELS`, which has carried the SMS row all along (S2, 16.09) —
    // not editing three near-identical blocks and hoping.
    //
    // ⚠️ AND NOTHING ABOUT THE TEXTS MOVED. Every sentence, every precedence rule and every
    // `null` return below is the one that was already there; this is the SHAPE changing, not the
    // behaviour. The channel labels are each channel's own existing §7 key.
    const channels = channelsFrom({ email: showEmailLine, whatsapp: active });

    const ROW: { [k in ChannelKey]?: () => { label: string; text: string; bad?: boolean; action?: React.ReactNode } | null } = {
        email: () => ({
            label: t('DataSources.send.whatsapp.receipt.email'),
            text: emailText,
        }),
        whatsapp: () => (wa.text === '' ? null : {
            label: t('DataSources.send.whatsapp.receipt.whatsapp'),
            text: wa.text,
            bad: wa.bad,
        }),
        // sms: — deliberately absent. An active channel with no row builder renders nothing
        // rather than crashing, and the absence is the honest state until SMS exists.
    };

    return (
        <Box className={classes.wrap}>
            {activeChannelKeys(channels).map((key) => {
                const row = ROW[key]?.();
                if (!row) return null;
                return (
                    <Box className={classes.row} key={key}>
                        <Typography variant="body2" className={classes.label}>{row.label}</Typography>
                        <Typography variant="body2" className={row.bad ? classes.bad : undefined}>
                            {row.text}
                        </Typography>
                        {row.action}
                    </Box>
                );
            })}

            {partialFailure && (
                <Typography variant="body2" className={classes.bad} role="status">
                    {t('DataSources.send.whatsapp.receipt.partialFailure')}
                </Typography>
            )}

            {sentToSupervisor && (
                <Box className={classes.row}>
                    <Typography variant="body2" className={classes.label}>
                        {t('DataSources.send.whatsapp.receipt.supervisor')}
                    </Typography>
                    <Typography variant="body2">
                        {/* `receipt.failed` is the WHATSAPP failure sentence and would read as
                            nonsense here, so the supervisor's failure arm reuses the screen's
                            existing generic error string rather than inventing a new key —
                            `send.whatsapp.*` may only contain keys the contract §7 lists. */}
                        {supervisorRequestId != null
                            ? t('DataSources.send.whatsapp.receipt.sent')
                            : t('DataSources.send.errors.generalError')}
                    </Typography>
                </Box>
            )}

            <Box className={classes.links}>
                <Link href={`${sitePrefix}SendSearch`} underline="always">
                    {t('DataSources.send.whatsapp.receipt.links.search')}
                </Link>
                <Link href={whatsappRoutes.REPORTS} underline="always">
                    {t('DataSources.send.whatsapp.receipt.links.waReport')}
                </Link>
            </Box>
        </Box>
    );
};

export default WaReceiptLines;
